import React from 'react';
import { OptionsMenuItem } from 'components/OptionsMenu/OptionsMenuItem';
import { DuplicateItemIcon } from 'components/icons';
import { t } from '@lingui/macro';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { FormQuestionType } from 'components/icons/QuestionIcon';
import { DuplicateOption_node$key } from './__generated__/DuplicateOption_node.graphql';
import {
  DuplicateOption_createFolderItems_Mutation,
  FormInput,
} from './__generated__/DuplicateOption_createFolderItems_Mutation.graphql';

const nodeFragment = graphql`
  fragment DuplicateOption_node on Node {
    __typename
    ... on FolderNodeInterface {
      parent: ancestors(depth: 1) {
        edges {
          node {
            ... on Folder {
              id
            }
          }
        }
      }
    }
    ... on FolderItem {
      id
      name
      content {
        __typename
        ... on Form {
          id
          questions {
            id
            text
            required
            definition {
              __typename
              id
              ... on CustomField__Checkboxes {
                options {
                  value
                  text
                }
              }
              ... on CustomField__Numeric {
                maxValue
                minValue
              }
              ... on CustomField__MultipleChoice {
                options {
                  value
                  text
                }
              }
            }
          }
        }
      }
    }
  }
`;

const createFolderItemsMutation = graphql`
  mutation DuplicateOption_createFolderItems_Mutation(
    $parentFolderId: ID!
    $contents: [FolderItemContentInput!]!
    $connections: [ID!]!
  ) {
    createFolderItems(
      input: { parentFolderId: $parentFolderId, contents: $contents }
    ) {
      folderItemsAdded
        @appendNode(connections: $connections, edgeTypeName: "FolderNodeEdge") {
        id
        content {
          ... on Form {
            id
          }
        }
        ...ItemMenu_node
      }
    }
  }
`;

interface DuplicateOptionProps {
  node: DuplicateOption_node$key;
  connectionId?: string;
}

export const DuplicateOption = ({
  node,
  connectionId,
}: DuplicateOptionProps) => {
  const item = useFragment(nodeFragment, node);

  const [createFolderItems, createFolderItemsLoading] =
    useMutation<DuplicateOption_createFolderItems_Mutation>(
      createFolderItemsMutation,
    );

  /* Show duplicate option only for forms */
  if (item.__typename !== 'FolderItem' || item.content?.__typename !== 'Form') {
    return null;
  }

  const { name, content, parent } = item;
  const parentFolderId =
    (parent?.edges && parent?.edges.length > 0 && parent?.edges[0]?.node?.id) ||
    '';

  function handleDuplicateClick() {
    const questions: FormInput['questions'] = content.questions
      .map(({ text, required, definition }) => {
        if (!definition) {
          return;
        }

        let newDefinition = {};
        switch (definition.__typename) {
          case FormQuestionType.TEXT:
            newDefinition = {
              stringDefinition: {},
            };
            break;
          case FormQuestionType.NUMERIC:
            newDefinition = {
              numericDefinition: {
                minValue: definition.minValue || 0,
                maxValue: definition.maxValue || 0,
              },
            };
            break;
          case FormQuestionType.CHECKBOXES:
            newDefinition = {
              checkboxesDefinition: {
                options: definition.options || [],
              },
            };
            break;
          case FormQuestionType.MULTIPLECHOICE:
            newDefinition = {
              multipleChoiceDefinition: {
                options: definition.options || [],
              },
            };
            break;
          case FormQuestionType.EMAIL:
            newDefinition = {
              emailDefinition: {},
            };
            break;

          case FormQuestionType.PHONE:
            newDefinition = {
              phoneNumberDefinition: {},
            };
            break;
          case FormQuestionType.LINK:
            newDefinition = {
              linkDefinition: {},
            };
            break;
          case FormQuestionType.DATE:
            newDefinition = {
              dateDefinition: {},
            };
            break;
          case FormQuestionType.TIME:
            newDefinition = {
              timeDefinition: {},
            };
            break;
          case FormQuestionType.DATETIME:
            newDefinition = {
              dateTimeDefinition: {},
            };
            break;
        }

        return {
          text,
          required,
          ...newDefinition,
        };
      })
      .filter(notEmpty);

    createFolderItems({
      variables: {
        parentFolderId,
        contents: [
          {
            form: {
              title: `${name} copy`,
              questions,
            },
            published: false,
          },
        ],
        connections: connectionId ? [connectionId] : [],
      },
    });
  }

  return (
    <OptionsMenuItem
      icon={<DuplicateItemIcon />}
      onClick={handleDuplicateClick}
      isDisabled={createFolderItemsLoading}
    >
      {t`Duplicate`}
    </OptionsMenuItem>
  );
};
